// features/workstations/property-dossier/comps-table.js
// ═══════════════════════════════════════════════════════════
// Renders the comparable-sales table inside the Deal Dossier so
// the user can SEE and vet each comp — distance, size, recency,
// $/sqft, and similarity — not just trust a single ARV number.
//
// Comp shape (from sources.js): { price, sqft, beds, baths,
//   distanceMi, daysOld, correlation, address }. Fields may be
//   missing (mock comps, or MLS comps with closeDate) — render '—'.
// ═══════════════════════════════════════════════════════════

const money = (n) => '$' + Math.round(Number(n) || 0).toLocaleString();

function daysAgo(d) {
  const n = Number(d);
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n < 31) return `${n}d ago`;
  if (n < 365) return `${Math.round(n / 30)}mo ago`;
  return `${(n / 365).toFixed(1)}y ago`;
}

export default function CompsTable({ comps = [], subjectSqft, subjectArv }) {
  if (!Array.isArray(comps) || comps.length === 0) {
    return <div style={{ fontSize: 13, color: '#888' }}>No comparable sales returned.</div>;
  }

  const subjectPpsf = subjectSqft > 0 && subjectArv > 0 ? Math.round(subjectArv / subjectSqft) : null;

  // Best matches first: highest similarity, then nearest.
  const sorted = [...comps].sort(
    (a, b) => (b.correlation ?? -1) - (a.correlation ?? -1) || (a.distanceMi ?? 99) - (b.distanceMi ?? 99)
  );

  return (
    <div className="comps-table-wrap">
      {subjectPpsf && (
        <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
          Subject ≈ <b style={{ color: '#bbb' }}>${subjectPpsf}/sqft</b> (ARV ÷ sqft) — compare each comp’s $/sqft against this.
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table className="comps-table">
          <thead>
            <tr>
              <th>Address</th><th>Bd/Ba</th><th>Sqft</th><th>$/sqft</th><th>Dist</th><th>Sold</th><th>Match</th><th className="r">Price</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((c, i) => {
              const ppsf = c.sqft > 0 && c.price > 0 ? Math.round(c.price / c.sqft) : null;
              const weak = c.correlation != null && c.correlation < 0.5;          // low similarity
              const far = c.distanceMi != null && c.distanceMi > 1.5;             // outside tight radius
              const dim = weak || far;
              const matchColor = c.correlation == null ? '#777'
                : c.correlation >= 0.7 ? '#7a9e82'
                : c.correlation >= 0.5 ? '#c8a96e' : '#c46050';
              return (
                <tr key={i} style={{ color: dim ? '#777' : 'inherit' }}>
                  <td title={dim ? 'Weaker comp (far or low similarity)' : ''}>{c.address || '—'}</td>
                  <td>{(c.beds || '—')}/{(c.baths || '—')}</td>
                  <td>{c.sqft ? c.sqft.toLocaleString() : '—'}</td>
                  <td>{ppsf ? `$${ppsf}` : '—'}</td>
                  <td>{c.distanceMi != null ? `${c.distanceMi}mi` : '—'}</td>
                  <td>{daysAgo(c.daysOld)}</td>
                  <td style={{ color: matchColor, fontWeight: 600 }}>{c.correlation != null ? `${Math.round(c.correlation * 100)}%` : '—'}</td>
                  <td className="r" style={{ fontWeight: 600 }}>{money(c.price)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: '#666', marginTop: 10 }}>
        Sorted by similarity. Dimmed rows are weaker comps (&gt;1.5mi away or low similarity) — discount them. “Match” is the model’s similarity score; verify the top comps against MLS/county records before underwriting.
      </div>
      <style jsx>{`
        .comps-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .comps-table th { text-align: left; color: #888; font-weight: 500; padding: 6px 8px; border-bottom: 1px solid var(--border-3); white-space: nowrap; }
        .comps-table td { padding: 7px 8px; border-top: 1px solid var(--border-4); white-space: nowrap; }
        .comps-table .r { text-align: right; }
        .comps-table td:first-child { white-space: normal; min-width: 160px; }
      `}</style>
    </div>
  );
}
